import type { RcFile, UploadProps } from "antd/es/upload";
import FileIcon from "assets/images/upstyled/file_icon.jpeg";
import PDFIcon from "assets/images/upstyled/pdf_icon.jpeg";
import defaultStyleImage from "assets/images/upstyled/default_background.png";

import ArticleIcon from "@mui/icons-material/Article";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FolderZipIcon from "@mui/icons-material/FolderZip";

import Compressor from "compressorjs";

import { nanoid } from "nanoid";
import toast from "react-hot-toast";

import api from "services/api";

type ImageFile = {
  url: string;
  type: string;
  name: string;
  preview?: string;
  is_primary?: boolean;
  imgurl?: any[];
};

type ImageResizeOptionType = {
  width?: number;
  height?: number;
  defaultUrl?: string;
  type: string;
  resizeLimit: "0" | "1";
};

const MAX_IMAGE_SIZE = 40;
const MAX_VIDEO_SIZE = 200;

export const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({
        width: img.width,
        height: img.height,
      });
    img.onerror = (error) => reject(error);
    img.src = url;
  });
};

export const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  if (mime == "image/jpeg") {
    filename = filename + ".jpg";
  } else if (mime == "image/png") {
    filename = filename + ".png";
  } else if (mime == "video/mp4") {
    filename = filename + ".mp4";
  }

  return new File([u8arr], filename, { type: mime });
};

export const matchYoutubeUrl = (url) => {
  const p =
    /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
  const matches = url.match(p);
  if (matches) {
    return true;
  }
  return false;
};

export const canResizeImageUrl = (type) => {
  if (!type || typeof type != "string") {
    return false;
  }

  if (type.match(/(image\/(jpeg|png|bmp|webp|jpg|\*))/g)) {
    //tiff and gif doesn't support ratio resize
    return true;
  } else {
    return false;
  }
};

export const formatFileThumbnail = (type) => {
  let icon = <ArticleIcon />;
  if (typeof type == "string") {
    if (type.match("application/pdf")) {
      icon = <PictureAsPdfIcon />;
    } else if (type.match("application/zip")) {
      icon = <FolderZipIcon />;
    }
  }
  return icon;
};

export const handleUploadFiles = async (files, config = {}, directory = "") => {
  //if already have url, then we won't upload it again
  const newNameMap = {};
  const newTypeMap = {};
  let newFiles = [...files];
  let uploadQueue: any[] = [];
  let oldFiles: any[] = [];
  for (let i = 0; i < newFiles.length; i++) {
    if (!newFiles[i].url) {
      // deal with dropped files
      let ext = newFiles[i].name.slice(newFiles[i].name.lastIndexOf("."));
      let newFileName = nanoid() + ext;
      newNameMap[newFileName] = newFiles[i].name;
      newTypeMap[newFileName] = newFiles[i].type;
      newFiles[i] = new File([newFiles[i]], newFileName, { type: newFiles[i].type });
      uploadQueue.push(newFiles[i]);
    } else {
      //won't do anything for existing files
      //existing file format {name:"xxx.xx",type:"image/png",url:"xxx"}
      oldFiles.push(newFiles[i]);
    }
  }

  try {
    if (uploadQueue && uploadQueue.length > 0) {
      const uploadedUrls = await api.uploads
        .create(uploadQueue, config, directory)
        .then(({ data }) => {
          const uploaded = data.uploads.map(({ url }) => {
            let dirArr = url.split("/");
            let name = dirArr[dirArr.length - 1];
            return { name: newNameMap[name], url: url, type: newTypeMap[name] };
          });
          return uploaded;
        });
      return [...oldFiles, ...uploadedUrls];
    } else {
      return [...oldFiles];
    }
  } catch (err) {
    console.log(err);
    return [];
  }
};

export const beforeUpload = (file) => {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    toast.error("You can only upload JPG/PNG file!");
  }
  const isLt2M = file.size / 1024 / 1024 < MAX_IMAGE_SIZE;
  if (!isLt2M) {
    toast.error(`Image must smaller than ${MAX_IMAGE_SIZE}MB!`);
  }
  return isJpgOrPng && isLt2M;
};

export const beforeUploadWithViedo = (file) => {
  if (file.type === "image/jpeg" || file.type === "image/png") {
    const isLt2M = file.size / 1024 / 1024 < MAX_IMAGE_SIZE;
    if (!isLt2M) {
      toast.error(`Image must smaller than ${MAX_IMAGE_SIZE}MB!`);
      return false;
    }
  } else if (file.type === "video/mp4") {
    const isLt60M = file.size / 1024 / 1024 < MAX_VIDEO_SIZE;
    if (!isLt60M) {
      toast.error(`Video must smaller than ${MAX_VIDEO_SIZE}MB!`);
      return false;
    }
  } else {
    toast.error("You can only upload JPG/PNG/MP4 file!");
  }

  return true;
};

export const uploadFileSizeLimit = (files) => {
  let filteredFiles: ImageFile[] = [];
  let oversizedFiles: ImageFile[] = [];
  for (let i = 0; i < files.length; i++) {
    if (files[i].type.match("image/*") || files[i].type.match("audio/*")) {
      if (files[i].size / 1024 / 1024 > MAX_IMAGE_SIZE) {
        oversizedFiles.push(files[i]);
      } else {
        filteredFiles.push(files[i]);
      }
    } else if (files[i].type.match("video/*")) {
      if (files[i].size / 1024 / 1024 > MAX_VIDEO_SIZE) {
        oversizedFiles.push(files[i]);
      } else {
        filteredFiles.push(files[i]);
      }
    } else {
      filteredFiles.push(files[i]);
    }
  }
  if (oversizedFiles.length > 0) {
    let names = oversizedFiles.map((x) => x?.name);
    toast.error(
      `File(s) ${names.join(",")} ${
        names.length > 1 ? "are" : "is"
      } oversized! Image/Audio should less than ${MAX_IMAGE_SIZE}MB, Video shoud less than ${MAX_VIDEO_SIZE}MB`
    );
  }

  return filteredFiles;
};

//if defaultImage=true, assign a default image to styles if there is no image
//style->sample images has been move to Li's work(referenceFiles)
export const getStyleImages = (
  product,
  defaultImage = false,
  metaFilesKey = ["imageFiles", "techPacksFiles", "referenceFiles"]
) => {
  const styleMeta = product?.style ? product.style : {};
  let styleImages: any[] = [];

  for (let i = 0; i < metaFilesKey.length; i++) {
    if (metaFilesKey[i] == "techPacksFiles") {
      //online techpack files from Li
      const onlineTechPackImages = getOnLineTechPackImages(product);
      if (onlineTechPackImages.length > 0) {
        styleImages.push(...onlineTechPackImages);
      }
    }

    let fileList = styleMeta[metaFilesKey[i]] || [];
    //convert Li's image type to standard image type(imgurl=>url)
    if (metaFilesKey[i] == "referenceFiles") {
      //referenceFiles managed by Li
      fileList = fileList.map((x) => {
        if (x.imgurl && x.imgurl.length > 0) {
          x["url"] = x.imgurl[0];
        }
        return x;
      });
    }

    //filter out unsupported data types
    const files = fileList.filter(
      (f) => f?.type && (f.type.includes("image") || f.type == "application/pdf")
    );
    if (files && files.length > 0) {
      styleImages.push(...files);
    }
  }

  if (styleImages.length == 0 && defaultImage) {
    styleImages = [{ url: defaultStyleImage, type: "image/jpg" }];
  }
  return styleImages;
};

//input: file:{type:xxx,url:xxx,name:xxx,size:xxx}
//ouput: url
export const getFileImage = (
  image: ImageFile,
  options: ImageResizeOptionType = { defaultUrl: "", type: "m_mfit", resizeLimit: "1" }
  //options = { height: 240, width: 200, defaultUrl: "" }
) => {
  //check Li's image format
  if (image.imgurl && image.imgurl.length > 0) {
    image["url"] = image.imgurl[0];
  }
  const random = new Date().valueOf();
  let url = "";
  if (image?.url && canResizeImageUrl(image?.type)) {
    url =
      image.url +
      `?random=${random}&x-oss-process=image/resize,${options.type}${
        options.height ? `,h_${options.height}` : ""
      }${options.width ? `,w_${options.width}` : ""}${
        options.resizeLimit != null ? `,limit_${options.resizeLimit}` : ""
      }`;
  } else if (image?.url && image.type?.match("image/*")) {
    //image files but can't be resize by cloud service
    url = image.url;
  } else if (image.type?.match("image/*") && image?.preview) {
    url = image.preview; //display preview image(base64)
  } else if (image.type?.match("application/pdf")) {
    url = PDFIcon;
  } else if (image.url && !image.type) {
    url = image.url;
  }

  if (!url && options?.defaultUrl) {
    url = options?.defaultUrl;
  } else if (!url) {
    url = FileIcon; //using file icon image as default image
  }

  return url;
};

export const getCollectionPrimaryImage = (moodboard: any) => {
  let primaryImage = moodboard.components
    ?.sort((a, b) => {
      return a.ts > b.ts;
    })
    .find((x) => x.type == "image");
  if (!primaryImage) {
    return null;
  }
  return {
    url: primaryImage.src,
    type: primaryImage.filetype,
    name: primaryImage.name,
    is_primary: true,
  };
};

//if defaultImage=true, assign a default image to styles if there is no image
export const getTechPackImages = (product, defaultImage = false) => {
  const styleMeta = product?.style ? product.style : {};
  let styleImages: any[] = [];

  //offline techpacks
  const metaFilesKey = ["techPacksFiles"];
  for (let i = 0; i < metaFilesKey.length; i++) {
    const fileList = styleMeta[metaFilesKey[i]] || [];
    const files = fileList.filter((f) => f?.type && f.type.includes("image"));
    if (files && files.length > 0) {
      styleImages.push(...files);
    }
  }
  //online techpack files from Li
  const onlineTechPackImages = getOnLineTechPackImages(product);
  if (onlineTechPackImages.length > 0) {
    styleImages.push(...onlineTechPackImages);
  }

  if (styleImages.length == 0 && defaultImage) {
    styleImages = [{ url: defaultStyleImage }];
  }
  return styleImages;
};

export const getOnLineTechPackImages = (product) => {
  const styleMeta = product?.style ? product.style : {};
  let images: any[] = [];

  //online techpack files
  const techpackBoards = styleMeta?.techpacks?.boards || [];
  for (let j = 0; j < techpackBoards.length; j++) {
    let sketchdatas = techpackBoards[j]?.sketchdatas || [];

    sketchdatas = sketchdatas
      .filter((x) => x.imgurl)
      .map((x) => {
        if (x.imgurl && x.imgurl.length > 0) {
          x["url"] = x.imgurl[0];
          x["type"] = "image/*";
        }
        return x;
      });
    images.push(...sketchdatas);
  }
  return images;
};

export const getBase64ImageFromUrl = (imageUrl) => {
  return new Promise((resolve, reject) => {
    fetch(imageUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result;
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });
};

export const resizeImg = (file, width, height) => {
  return new Promise((resolve, _) => {
    new Compressor(file, {
      strict: false,
      width: width,
      height: height,
      resize: "contain",
      quality: 0.92,
      mimeType: "image/jpeg",
      success: async (result) => {
        resolve(result);
      },
      error(err) {
        toast.error(err.message);
      },
    });
  });
};
